import {Fragment, type ReactNode, useState} from 'react'
import {Link as RouterLink, useLocation} from 'react-router-dom'
import {trackEvent} from '../../../common/analytics/trackEvent'
import type {CtaBlock, CtaSecondaryLink, FaqEntry, ScreenshotPlaceholder} from './seoTypes'

export const PlusIcon = ({size = 16}: {size?: number}) => (
  <svg
    className="seo-faq-icon"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
)

interface FaqSectionProps {
  title: string
  faq: readonly FaqEntry[]
}

// Accordion FAQ. The matching FAQPage JSON-LD is emitted separately by the page
// via <Seo jsonLd={...}>, so this is purely the visible UI.
export const FaqSection = ({title, faq}: FaqSectionProps) => {
  const [openIdx, setOpenIdx] = useState<number | null>(0)
  return (
    <section className="seo-section" aria-label={title}>
      <h2 className="seo-h2">{title}</h2>
      <div className="seo-faq">
        {faq.map((entry, idx) => {
          const isOpen = openIdx === idx
          return (
            <div key={entry.q} className={`seo-faq-item ${isOpen ? 'open' : ''}`}>
              <button
                type="button"
                className="seo-faq-btn"
                aria-expanded={isOpen}
                onClick={() => setOpenIdx(isOpen ? null : idx)}
              >
                <span>{entry.q}</span>
                <PlusIcon size={14} />
              </button>
              {isOpen && <div className="seo-faq-body">{entry.a}</div>}
            </div>
          )
        })}
      </div>
    </section>
  )
}

// `cover` (default) crops to a 16/10 tile — fine for landscape editor art.
// `contain` shows the whole image letterboxed — for real portrait/landscape
// device screenshots that must not be cropped.
export const ScreenshotGrid = ({
  shots,
  variant = 'cover',
}: {
  shots: readonly ScreenshotPlaceholder[]
  variant?: 'cover' | 'contain'
}) => {
  if (shots.length === 0) return null
  return (
    <div className={`seo-shots ${variant === 'contain' ? 'seo-shots--contain' : ''}`}>
      {shots.map((shot) => (
        <figure key={shot.src} className={`seo-figure ${shot.wide ? 'seo-figure--wide' : ''}`}>
          <img src={shot.src} alt={shot.alt} loading="lazy" />
          <figcaption>{shot.caption}</figcaption>
        </figure>
      ))}
    </div>
  )
}

const DEFAULT_SECONDARY_LINKS: readonly CtaSecondaryLink[] = [{to: '/icon-generator', label: 'Free app icon generator'}]

// secondaryLinks defaults to the icon-generator pitch so every existing guide/
// comparison call site (and every free-tool page's own bottom CTA) keeps its
// current behavior untouched — pass an override when the default pitch isn't
// actually relevant to the page's topic (e.g. a guide about a different tool).
export const CtaSection = ({
  cta,
  secondaryLinks = DEFAULT_SECONDARY_LINKS,
}: {
  cta: CtaBlock
  secondaryLinks?: readonly CtaSecondaryLink[]
}) => {
  const location = useLocation()
  const trackClick = (target: string, label: string) =>
    trackEvent('guide_cta_clicked', {source_path: location.pathname, destination: target, label})
  return (
    <section className="seo-cta">
      <h2 className="seo-cta-title">{cta.title}</h2>
      <p className="seo-cta-body">{cta.body}</p>
      <div className="seo-cta-actions">
        <RouterLink to="/free" className="seo-btn-primary" onClick={() => trackClick('/free', 'primary')}>
          Start free in your browser
        </RouterLink>
        {secondaryLinks.map((link) => (
          <RouterLink
            key={link.to}
            to={link.to}
            className="seo-btn-secondary"
            onClick={() => trackClick(link.to, 'secondary')}
          >
            {link.label}
          </RouterLink>
        ))}
      </div>
    </section>
  )
}

interface RelatedLink {
  path: string
  label: string
}

export const RelatedLinks = ({title, links}: {title: string; links: readonly RelatedLink[]}) => {
  const location = useLocation()
  return (
    <section className="seo-section" aria-label={title}>
      <h2 className="seo-h2">{title}</h2>
      <div className="seo-links">
        {links.map((link) => (
          <RouterLink
            key={link.path}
            to={link.path}
            className="seo-link-chip"
            onClick={() =>
              trackEvent('guide_related_link_clicked', {source_path: location.pathname, destination: link.path})
            }
          >
            {link.label} →
          </RouterLink>
        ))}
      </div>
    </section>
  )
}

// Markdown-style internal link inside page copy: "[anchor text](/guides/slug)".
// Only site-relative paths are linked; anything else stays plain text.
const INLINE_LINK_RE = /\[([^\]]+)\]\((\/[^)\s]*)\)/g

// Renders a copy string, turning inline "[anchor](/path)" markers into contextual
// internal links (the SEO internal-linking pass writes these into guide copy).
export const InlineText = ({text}: {text: string}) => {
  const location = useLocation()
  const parts: ReactNode[] = []
  let cursor = 0
  for (const match of text.matchAll(INLINE_LINK_RE)) {
    const [whole, label, to] = match
    if (match.index > cursor) parts.push(<Fragment key={`t${cursor}`}>{text.slice(cursor, match.index)}</Fragment>)
    parts.push(
      <RouterLink
        key={`l${match.index}`}
        to={to}
        className="seo-inline-link"
        onClick={() =>
          trackEvent('guide_related_link_clicked', {
            source_path: location.pathname,
            destination: to,
            placement: 'inline',
          })
        }
      >
        {label}
      </RouterLink>,
    )
    cursor = match.index + whole.length
  }
  if (cursor < text.length) parts.push(<Fragment key={`t${cursor}`}>{text.slice(cursor)}</Fragment>)
  return <>{parts}</>
}
